import {FeatureToggle} from '@/config/featureToggles'
import {isFeatureEnabled} from '@/helpers/featureToggle'
import {InputSelector} from '@/shared-components/InputSelector/InputSelector'
import {RemoveParameterButton} from '@/shared-components/RemoveParameterButton'
import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {
  BooleanOperatorsSchema,
  BooleanParams,
  CategoryType,
  DataParams,
  DateOperatorsSchema,
  DateParams,
  EnumOperatorsSchema,
  EnumParams,
  IntegerOperatorsSchema,
  IntegerParams,
  OperatorTypeSchema,
  Operators,
  ParamsSelector,
  StringOperatorsSchema,
  StringParams,
} from '../../../types/paramFilters/paramFilters'
import {
  ParamsItem,
  categoryParams,
  getParamByValue,
  getTraitOptions,
  isOperatorBoolean,
  isOperatorList,
} from '../../../utils/consts'
import {ChangeFilterValueProps, FilterOptionsByParam} from './FilterOptionsByParam'

interface Props {
  position: number
  isPublished: boolean
  hasMultipleParamsSelectors: boolean
  currentParamSelector: ParamsSelector
  updateSelectedParam: (id: number, updatedParamSelector: ParamsSelector | null) => void
  assignedSegmentList?: string[]
}

export const ParamSelector: React.FC<Props> = ({
  position,
  isPublished,
  currentParamSelector,
  hasMultipleParamsSelectors,
  updateSelectedParam,
  assignedSegmentList,
}) => {
  const {selectedCategory: tempSelectedCategory, selectedParam, id} = currentParamSelector
  const [selectedCategory, setSelectedCategory] = useState<string>(tempSelectedCategory)
  const [traitList, setTraitList] = useState<ParamsItem[]>(getTraitOptions(tempSelectedCategory))
  const isFirstParamSelector = position === 0

  const shouldDisplayRemoveButton =
    !isPublished && (hasMultipleParamsSelectors || (isFirstParamSelector && selectedParam !== ''))

  const {formatMessage} = useIntl()

  const onSelectNewCategory = (newCategory: CategoryType) => {
    if (newCategory === '') {
      return
    }
    setSelectedCategory(newCategory)
    const updatedSelectedParam = {...currentParamSelector, selectedCategory: newCategory}
    updateSelectedParam(id, updatedSelectedParam)
    const newOptions = getTraitOptions(newCategory)
    setTraitList(newOptions)
    onSelectNewParam(newOptions[0].value, updatedSelectedParam)
  }

  const onSelectNewParam = (newParam: DataParams, updatedCategoryParamSelector?: ParamsSelector) => {
    if (isPublished) return

    let updatedSelectedParam: ParamsSelector
    const selectedType = getParamByValue(newParam)

    if (isFeatureEnabled(FeatureToggle.assignedSegmentList) && selectedType.value === 'assignedSegments') {
      selectedType.type = OperatorTypeSchema.Values.enum
    }

    const tempParamsSelectors = updatedCategoryParamSelector ?? currentParamSelector
    switch (selectedType.type) {
      case OperatorTypeSchema.Values.boolean:
        updatedSelectedParam = {
          ...tempParamsSelectors,
          selectedParamType: selectedType.type,
          selectedParam: newParam as BooleanParams,
          selectedParamData: {
            operator: BooleanOperatorsSchema.Values.isNo,
            value: null,
          },
        }
        break
      case OperatorTypeSchema.Values.integer:
        updatedSelectedParam = {
          ...tempParamsSelectors,
          selectedParamType: selectedType.type,
          selectedParam: newParam as IntegerParams,
          selectedParamData: {
            operator: IntegerOperatorsSchema.Values.lessThan,
            value: null,
          },
        }
        break
      case OperatorTypeSchema.Values.date:
        updatedSelectedParam = {
          ...tempParamsSelectors,
          selectedParamType: selectedType.type,
          selectedParam: newParam as DateParams,
          selectedParamData: {
            operator: DateOperatorsSchema.Values.lessThan,
            value: null,
          },
        }
        break
      case OperatorTypeSchema.Values.enum:
        updatedSelectedParam = {
          ...tempParamsSelectors,
          selectedParamType: selectedType.type,
          selectedParam: newParam as EnumParams,
          selectedParamData: {
            operator: EnumOperatorsSchema.Values.equalTo,
            value: null,
          },
        }
        break
      case OperatorTypeSchema.Values.string:
      default:
        updatedSelectedParam = {
          ...tempParamsSelectors,
          selectedParamType: selectedType.type,
          selectedParam: newParam as StringParams,
          selectedParamData: {
            operator: StringOperatorsSchema.Values.equalTo,
            value: null,
          },
        }
        break
    }

    updateSelectedParam(id, updatedSelectedParam)
  }

  const shouldSetOperatorValueToNull = (newOperator: Operators, oldOperator: Operators) => {
    return (
      isOperatorBoolean(newOperator) ||
      (isOperatorList(newOperator) && !isOperatorList(oldOperator)) ||
      (!isOperatorList(newOperator) && isOperatorList(oldOperator))
    )
  }

  const onChangeFilterValue = ({key, newValue}: ChangeFilterValueProps) => {
    if (!selectedParam || isPublished) return

    const updatedSelectedParam = {
      ...currentParamSelector,
      selectedParamData: {...currentParamSelector.selectedParamData, [key]: newValue},
    } as ParamsSelector

    if (key === 'operator') {
      const oldOperator = currentParamSelector.selectedParamData.operator

      if (shouldSetOperatorValueToNull(newValue, oldOperator)) {
        updatedSelectedParam.selectedParamData.value = null
      }
    }

    if (key === 'value' && newValue === '') {
      updatedSelectedParam.selectedParamData.value = null
    }

    updateSelectedParam(id, updatedSelectedParam)
  }

  const removeCurrentSelector = () => {
    updateSelectedParam(id, null)
  }

  const category = useMemo(() => {
    return categoryParams
      .filter(categoryItem => {
        if (['order'].includes(categoryItem.value)) {
          return isFeatureEnabled(FeatureToggle.traitsOrders)
        }
        if (['points'].includes(categoryItem.value)) {
          return isFeatureEnabled(FeatureToggle.traitsPoints)
        }
        if (['progmotion'].includes(categoryItem.value)) {
          return isFeatureEnabled(FeatureToggle.traitsPromotions)
        }
        if (['target', 'campaign'].includes(categoryItem.value)) {
          return isFeatureEnabled(FeatureToggle.traitsTargets)
        }
        if (['registration'].includes(categoryItem.value)) {
          return isFeatureEnabled(FeatureToggle.registration)
        }
        if (['storeDemography'].includes(categoryItem.value)) {
          return isFeatureEnabled(FeatureToggle.storeDemography)
        }
        if (['monthlyActivity'].includes(categoryItem.value)) {
          return isFeatureEnabled(FeatureToggle.monthlyActivity)
        }
        return true
      })
      .sort((a, b) => formatMessage({id: a.displayValue}).localeCompare(formatMessage({id: b.displayValue})))
  }, [formatMessage])

  return (
    <>
      <div className="mb-4 flex gap-4 text-sm font-bold leading-6">
        {formatMessage({id: 'ENTRY_CONDITION_CONTENT.PARAMETER'}, {position: position + 1})}
        {shouldDisplayRemoveButton && <RemoveParameterButton iconSize="small" onClick={removeCurrentSelector} />}
      </div>
      <div className="relative flex w-full flex-wrap items-start justify-between gap-4">
        <div className="flex w-full">
          <div className="flex w-full max-w-[301px] flex-col gap-2">
            <InputSelector
              id="category"
              label={formatMessage({id: 'ENTRY_CONDITION_CONTENT.PARAMETER_CONDITION_CATEGORY'})}
              onChange={evt => onSelectNewCategory(evt.target.value as CategoryType)}
              value={selectedCategory}
              disabled={isPublished}
              required
            >
              <option hidden>{formatMessage({id: 'ENTRY_CONDITION_CONTENT.SELECT_A_CATEGORY'})}</option>
              {category.map(({value, displayValue}) => {
                return (
                  <option value={value} key={value} data-testid="category-option">
                    {formatMessage({id: displayValue})}
                  </option>
                )
              })}
            </InputSelector>
          </div>
        </div>

        <div className="flex w-full max-w-[301px] flex-col gap-2">
          <InputSelector
            id="param"
            label={formatMessage({id: 'ENTRY_CONDITION_CONTENT.PARAMETER_CONDITION_TRAIT'})}
            onChange={evt => onSelectNewParam(evt.target.value as DataParams)}
            value={selectedParam}
            disabled={isPublished}
            required
          >
            <option hidden>{formatMessage({id: 'ENTRY_CONDITION_CONTENT.SELECT_A_PARAMETER'})}</option>
            {traitList.map(({value, displayValue}) => {
              return (
                <option value={value} key={value} data-testid="param-option">
                  {formatMessage({id: displayValue})}
                </option>
              )
            })}
          </InputSelector>
        </div>

        <FilterOptionsByParam
          currentParamSelector={currentParamSelector}
          onChangeFilterValue={onChangeFilterValue}
          disabled={isPublished}
          assignedSegmentList={assignedSegmentList}
        />
      </div>
    </>
  )
}
